import React, { useState, useEffect } from 'react';
import { forecastingAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { TrendingUp, Target, DollarSign, BarChart3, Loader2, Shield, Zap } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';

const STAGE_COLORS = { Prospecting: '#64748b', Qualification: '#3b82f6', Proposal: '#f59e0b', Negotiation: '#8b5cf6', 'Closed Won': '#10b981' };

export default function ForecastingDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await forecastingAPI.getForecast('quarter'); setData(res.data); }
      catch { toast.error('Failed to load forecast'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const s = data?.summary || {};
  const scenarioData = [
    { name: 'Worst Case', value: s.worst_case || 0, color: '#ef4444' },
    { name: 'Weighted', value: s.weighted_pipeline || 0, color: '#f59e0b' },
    { name: 'Commit', value: s.commit || 0, color: '#3b82f6' },
    { name: 'Best Case', value: s.best_case || 0, color: '#10b981' },
  ];

  return (
    <div className="space-y-6" data-testid="forecasting-page">
      <div><h1 className="text-2xl font-bold tracking-tight">Revenue Forecasting</h1>
        <p className="text-sm text-muted-foreground mt-1">Weighted pipeline analysis with scenario modeling</p></div>

      {/* Scenario Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <ScenarioCard label="Commit" value={s.commit} icon={Shield} color="text-blue-500" desc="Deals at 75%+ probability" />
        <ScenarioCard label="Weighted Pipeline" value={s.weighted_pipeline} icon={Target} color="text-amber-500" desc="Stage-weighted totals" />
        <ScenarioCard label="Best Case" value={s.best_case} icon={TrendingUp} color="text-emerald-500" desc="All open deals close" />
        <ScenarioCard label="Won Revenue" value={s.won_revenue} icon={DollarSign} color="text-purple-500" desc="Already closed" />
      </div>

      {/* Coverage Ratio */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Pipeline Coverage Ratio</p>
              <p className="text-xs text-muted-foreground">Best case pipeline / won revenue</p></div>
            <div className="flex items-center gap-2">
              <span className={`text-3xl font-bold ${s.coverage_ratio >= 3 ? 'text-emerald-500' : s.coverage_ratio >= 2 ? 'text-amber-500' : 'text-red-500'}`}>
                {s.coverage_ratio}x
              </span>
              <span className="text-xs text-muted-foreground">{s.coverage_ratio >= 3 ? 'Healthy' : s.coverage_ratio >= 2 ? 'Adequate' : 'Low'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Scenario Comparison */}
        <Card>
          <CardHeader><CardTitle className="text-sm">Forecast Scenarios</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={scenarioData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={v => `$${v.toLocaleString()}`} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {scenarioData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Stage Forecast */}
        <Card>
          <CardHeader><CardTitle className="text-sm">Pipeline by Stage (Weighted)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data?.stage_forecast || []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                <XAxis dataKey="stage" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={v => `$${v.toLocaleString()}`} />
                <Legend />
                <Bar dataKey="total_value" fill="#3b82f6" name="Total Value" radius={[4, 4, 0, 0]} />
                <Bar dataKey="weighted_value" fill="#f59e0b" name="Weighted" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Stage Breakdown Table */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Stage Probability Breakdown</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-xs text-muted-foreground">
              <th className="text-left py-2 px-3">Stage</th><th className="text-right py-2 px-3">Probability</th>
              <th className="text-right py-2 px-3">Deals</th><th className="text-right py-2 px-3">Total Value</th>
              <th className="text-right py-2 px-3">Weighted Value</th></tr></thead>
            <tbody>
              {(data?.stage_forecast || []).map((s, i) => (
                <tr key={i} className="border-b border-muted/30" data-testid={`forecast-row-${i}`}>
                  <td className="py-2 px-3 font-medium">{s.stage}</td>
                  <td className="py-2 px-3 text-right"><span className="px-2 py-0.5 rounded-full text-xs bg-primary/10">{s.probability}%</span></td>
                  <td className="py-2 px-3 text-right">{s.deals}</td>
                  <td className="py-2 px-3 text-right">${(s.total_value || 0).toLocaleString()}</td>
                  <td className="py-2 px-3 text-right font-bold text-amber-500">${(s.weighted_value || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function ScenarioCard({ label, value, icon: Icon, color, desc }) {
  return (
    <Card data-testid={`scenario-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10"><Icon className={`h-4 w-4 ${color}`} /></div>
          <div><p className="text-xl font-bold">${((value || 0) / 1000).toFixed(1)}k</p>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-[10px] text-muted-foreground/70">{desc}</p></div>
        </div>
      </CardContent>
    </Card>
  );
}
